/**
 * Permissions Management Page
 *
 * Main admin page for managing roles and rights (permissions).
 * Features: Two-tab interface (Roles | Rights), full CRUD operations, assign rights to roles.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  IconShieldCheck,
  IconKey,
  IconShield,
  IconPlus,
  IconRefresh,
  IconAlertCircle,
} from '@tabler/icons-react';
import {
  Container,
  Stack,
  Card,
  Group,
  Button,
  Alert,
  Text,
  Loader,
  Grid,
  Tabs,
  Title,
} from '../../../components/ui';
import { FadeIn, SlideIn } from '../../../components/animations';
import { RolesTab } from './components/RolesTab';
import { RightsTab } from './components/RightsTab';
import { RoleModal } from './components/RoleModal';
import { RightModal } from './components/RightModal';
import { usePermissionManagement } from './hooks/usePermissionManagement';
import type { RoleModelView, RightModelView } from './types';

export default function PermissionsPage() {
  const permissionMgmt = usePermissionManagement();

  // State
  const [roles, setRoles] = useState<RoleModelView[]>([]);
  const [rights, setRights] = useState<RightModelView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('roles');

  // Modal states
  const [roleModalOpened, setRoleModalOpened] = useState(false);
  const [rightModalOpened, setRightModalOpened] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleModelView | null>(null);
  const [editingRight, setEditingRight] = useState<RightModelView | null>(null);

  /**
   * Fetch all data
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, rightsData] = await Promise.all([
        permissionMgmt.fetchRoles(),
        permissionMgmt.fetchRights(),
      ]);
      setRoles(rolesData);
      setRights(rightsData);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch permissions data';
      setError(errorMessage);
      console.error('Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Initial load
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Calculate stats
   */
  const stats = useMemo(() => {
    const totalRoles = roles.length;
    const totalRights = rights.length;
    const totalAssignments = roles.reduce((sum, role) => sum + (role.permissions?.length || 0), 0);
    const mostPrivilegedRole = roles.reduce(
      (max, role) => ((role.permissions?.length || 0) > (max.permissions?.length || 0) ? role : max),
      roles[0] || { name: 'N/A', permissions: [] }
    );

    return {
      totalRoles,
      totalRights,
      totalAssignments,
      mostPrivilegedRole: mostPrivilegedRole?.name || 'N/A',
    };
  }, [roles, rights]);

  /**
   * Handle role edit
   */
  const handleEditRole = useCallback((role: RoleModelView) => {
    setEditingRole(role);
    setRoleModalOpened(true);
  }, []);

  /**
   * Handle role delete
   */
  const handleDeleteRole = useCallback(
    async (role: RoleModelView) => {
      try {
        await permissionMgmt.deleteRole(role.id);
        await fetchData();
      } catch (err: any) {
        console.error('Error deleting role:', err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchData]
  );

  /**
   * Handle right edit
   */
  const handleEditRight = useCallback((right: RightModelView) => {
    setEditingRight(right);
    setRightModalOpened(true);
  }, []);

  /**
   * Handle right delete
   */
  const handleDeleteRight = useCallback(
    async (right: RightModelView) => {
      try {
        await permissionMgmt.deleteRight(right.id);
        await fetchData();
      } catch (err: any) {
        console.error('Error deleting right:', err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchData]
  );

  /**
   * Handle new role button
   */
  const handleNewRole = useCallback(() => {
    setEditingRole(null);
    setRoleModalOpened(true);
  }, []);

  /**
   * Handle new right button
   */
  const handleNewRight = useCallback(() => {
    setEditingRight(null);
    setRightModalOpened(true);
  }, []);

  /**
   * Handle role modal success
   */
  const handleRoleModalSuccess = useCallback(() => {
    setRoleModalOpened(false);
    setEditingRole(null);
    fetchData();
  }, [fetchData]);

  /**
   * Handle right modal success
   */
  const handleRightModalSuccess = useCallback(() => {
    setRightModalOpened(false);
    setEditingRight(null);
    fetchData();
  }, [fetchData]);

  /**
   * Handle role modal close
   */
  const handleRoleModalClose = useCallback(() => {
    setRoleModalOpened(false);
    setEditingRole(null);
  }, []);

  /**
   * Handle right modal close
   */
  const handleRightModalClose = useCallback(() => {
    setRightModalOpened(false);
    setEditingRight(null);
  }, []);

  return (
    <Container size="xl">
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Group gap="sm">
                <IconShieldCheck size={32} />
                <Title order={2}>Permissions Management</Title>
              </Group>
              <Text c="dimmed" size="sm">
                Manage roles and rights for the system
              </Text>
            </Stack>
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={fetchData}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>

          {/* Error Alert */}
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error" withCloseButton onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Stats Cards */}
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={0}>
                <Card variant="gradient" gradientVariant="primary" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Total Roles
                      </Text>
                      <IconShield size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.totalRoles}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={100}>
                <Card variant="gradient" gradientVariant="secondary" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Total Rights
                      </Text>
                      <IconKey size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.totalRights}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={200}>
                <Card variant="gradient" gradientVariant="info" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Total Assignments
                      </Text>
                      <IconShieldCheck size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.totalAssignments}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={300}>
                <Card variant="gradient" gradientVariant="success" padding="md">
                  <Stack gap="xs">
                    <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                      Most Privileged
                    </Text>
                    <Text size="lg" fw={600} c="white" truncate="end">
                      {stats.mostPrivilegedRole}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>
          </Grid>

          {/* Tabs */}
          <Card>
            <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'roles')} tabsVariant="pills">
              <Tabs.List>
                <Tabs.Tab value="roles" leftSection={<IconShield size={16} />}>
                  Roles
                </Tabs.Tab>
                <Tabs.Tab value="rights" leftSection={<IconKey size={16} />}>
                  Rights
                </Tabs.Tab>

                {/* New Button based on active tab */}
                <Group style={{ marginLeft: 'auto' }}>
                  {activeTab === 'roles' && (
                    <Button
                      variant="gradient"
                      gradientVariant="primary"
                      size="sm"
                      leftSection={<IconPlus size={16} />}
                      onClick={handleNewRole}
                    >
                      New Role
                    </Button>
                  )}
                  {activeTab === 'rights' && (
                    <Button
                      variant="gradient"
                      gradientVariant="primary"
                      size="sm"
                      leftSection={<IconPlus size={16} />}
                      onClick={handleNewRight}
                    >
                      New Right
                    </Button>
                  )}
                </Group>
              </Tabs.List>

              <Tabs.Panel value="roles" pt="md">
                {loading && roles.length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <RolesTab
                    roles={roles}
                    onRefresh={fetchData}
                    onEdit={handleEditRole}
                    onDelete={handleDeleteRole}
                    loading={loading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="rights" pt="md">
                {loading && rights.length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <RightsTab
                    rights={rights}
                    onRefresh={fetchData}
                    onEdit={handleEditRight}
                    onDelete={handleDeleteRight}
                    loading={loading}
                  />
                )}
              </Tabs.Panel>
            </Tabs>
          </Card>
        </Stack>
      </FadeIn>

      {/* Role Modal */}
      <RoleModal
        opened={roleModalOpened}
        onClose={handleRoleModalClose}
        onSuccess={handleRoleModalSuccess}
        role={editingRole}
      />

      {/* Right Modal */}
      <RightModal
        opened={rightModalOpened}
        onClose={handleRightModalClose}
        onSuccess={handleRightModalSuccess}
        right={editingRight}
      />
    </Container>
  );
}
